import { FilterOptionsState } from '@mui/material';
import Autocomplete, { AutocompleteChangeReason, createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

export interface Tag {
  label: string
}

export enum SearchType {
  AND,
  OR,
}

interface TagsInterface {
  label: string
  options: Tag[]
  value: Tag[]
  setValue: (newTags: Tag[]) => void
  searchType?: SearchType
}

// Custom filter to prioritize exact matches at the top of the list.
// Help from ChatGPT.
const filter = createFilterOptions<Tag>();
const score = (option: string, input: string) => {
  const o = option.toLowerCase();
  const i = input.toLowerCase();

  if (o === i) return 0;                 // exact
  if (o.startsWith(i)) return 1;         // prefix
  if (o.split(' ').includes(i)) return 2; // whole word
  if (o.includes(i)) return 3;           // substring
  return 4;
};

const filterOptions = (options: Tag[], state: FilterOptionsState<Tag>) => {
  const input = state.inputValue.trim();
  if (!input) return options;

  const filtered = filter(options, state);

  return filtered.sort(
    (a, b) => score(a.label, input) - score(b.label, input)
  );
};

export default function Tags(props: TagsInterface) {
  var colorSx = {}
  if (props.searchType !== undefined) {
    if (props.searchType === SearchType.OR) {
      colorSx = {
        '& .MuiOutlinedInput-root': {
          '& fieldset': {
            borderColor: 'blueviolet',
          },
        }
      }
    }
  }

  // On mobile, specifically Safari on iOS, when you select the input textfield for an Autocomplete,
  // the screen will scroll to accomdate the virtual keyboard, but most of the options will still be covered by the keyboard.
  // There is no way to close the keyboard and still see the options. I want on mobile for the input to scroll to the top.
  // Ideally I could just adjust whatever setting controls this scroll behaviour to the middle,
  // but after scowering the internet, I could not find the correct solution.
  // The best approach I have is to grab the mousedown event,
  // wait for the bad scroll to happen, and THEN scroll the input to the top of the screen.
  // This works really well on my iPhone. This is not super required on my iPad since a user can close
  // the onscreen keyboard and still view the options.
  // Apple iPad Pro 9.7 has viewport size 768 x 1024 Pixels and its pixel ratio is about 2.
  // On desktop, there is no on screen keyboard so no scroll is required in the first place.

  function handleOpen(event: React.SyntheticEvent) {
    var mouseEvent = event as React.MouseEvent // not every event is a moust event 
    if (mouseEvent.pageY === undefined) {
      return
    }

    // https://stackoverflow.com/questions/3514784/what-is-the-best-way-to-detect-a-mobile-device
    const isMobile = window.matchMedia("only screen and (max-width: 767px)").matches;
    if (isMobile) {
      setTimeout(function () {
        window.scrollTo({
          top: mouseEvent.pageY - 40,
          left: 0,
          behavior: "smooth"
        });
      }, 100);
    }
  }

  return (
    <Autocomplete
      multiple
      value={props.value} // TODO: Change rendering if value is not in options.
      onChange={(_event, newValue: Tag[], _reason: AutocompleteChangeReason) => {
        props.setValue(newValue);
      }}
      id="tags-outlined"
      options={props.options}
      getOptionLabel={(option) => option.label}
      filterOptions={filterOptions}
      filterSelectedOptions
      freeSolo={false} // TODO maybe make this possible?
      isOptionEqualToValue={(option, value) =>
        option.label === value.label
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={props.label}
          size={"small"}
          sx={colorSx}
        />
      )}
      autoComplete={true}
      autoHighlight={true}
      autoSelect={false}
      openOnFocus={true}
      onOpen={handleOpen}
    />
  );
}
